import type { UIMessage } from "ai";

import {
  BRAND_ASSISTANT_SELECTED_ASSET_REFERENCE_PART_TYPE,
  BRAND_ASSISTANT_SELECTED_IMAGE_REFERENCE_PART_TYPE,
  buildBrandAssistantSelectedAssetReferencePart,
  buildBrandAssistantSelectedImageReferencePart,
  buildBrandAssistantSuggestionActionPart,
  type BrandAssistantCreationPreviewToolName,
  type BrandAssistantSelectedAssetReference,
  type BrandAssistantSelectedImageReference,
} from "@runcheetah/shared";

import type { AssistantRouteCatalogEntry } from "./app-route-manifest";

type BrandAssistantChatRequestTrigger = "submit-message" | "regenerate-message";

const BRAND_ASSISTANT_SEND_REQUEST_CONTEXT_KEY = "brandAssistantSendRequestContext";

export type BrandAssistantSendRequestContext = {
  chatId: string;
  selectedAssetReferences?: readonly BrandAssistantSelectedAssetReference[];
  selectedImageReferences?: readonly BrandAssistantSelectedImageReference[];
};

export const buildBrandAssistantUserPromptParts = (input: {
  prompt: string;
  selectedAssetReferences?: readonly BrandAssistantSelectedAssetReference[];
  selectedImageReferences?: readonly BrandAssistantSelectedImageReference[];
  creationTool?: BrandAssistantCreationPreviewToolName | null;
}): Array<
  | ReturnType<typeof buildBrandAssistantSelectedAssetReferencePart>
  | ReturnType<typeof buildBrandAssistantSelectedImageReferencePart>
  | ReturnType<typeof buildBrandAssistantSuggestionActionPart>
  | { type: "text"; text: string }
> => [
  ...(input.selectedAssetReferences ?? []).map((reference) =>
    buildBrandAssistantSelectedAssetReferencePart(reference),
  ),
  ...(input.selectedImageReferences ?? []).map((reference) =>
    buildBrandAssistantSelectedImageReferencePart(reference),
  ),
  ...(input.creationTool ? [buildBrandAssistantSuggestionActionPart(input.creationTool)] : []),
  { type: "text", text: input.prompt },
];

export const buildBrandAssistantSendRequestOptionsBody = (
  context: BrandAssistantSendRequestContext,
): Record<string, unknown> => ({
  [BRAND_ASSISTANT_SEND_REQUEST_CONTEXT_KEY]: context,
});

export const getBrandAssistantSendRequestContext = (
  body: Record<string, unknown> | undefined,
): BrandAssistantSendRequestContext | null => {
  const context = body?.[BRAND_ASSISTANT_SEND_REQUEST_CONTEXT_KEY];
  if (
    !context ||
    typeof context !== "object" ||
    !("chatId" in context) ||
    typeof context.chatId !== "string"
  ) {
    return null;
  }

  return context as BrandAssistantSendRequestContext;
};

export type BrandAssistantCurrentPageContext = {
  routeId: string;
  label: string;
  description: string;
  href: string;
  pathTemplate: string;
  params: Record<string, string>;
};

export type { BrandAssistantSelectedAssetReference };
export type { BrandAssistantSelectedImageReference };

export const attachSelectedReferencesToUserMessage = (
  message: UIMessage | undefined,
  selectedAssetReferences: readonly BrandAssistantSelectedAssetReference[] | undefined,
  selectedImageReferences: readonly BrandAssistantSelectedImageReference[] | undefined,
): UIMessage | undefined => {
  if (
    !message ||
    message.role !== "user" ||
    (!selectedAssetReferences?.length && !selectedImageReferences?.length)
  ) {
    return message;
  }

  return {
    ...message,
    parts: [
      ...message.parts.filter(
        (part) =>
          !isBrandAssistantSelectedReferencePartType(part, [
            BRAND_ASSISTANT_SELECTED_ASSET_REFERENCE_PART_TYPE,
            BRAND_ASSISTANT_SELECTED_IMAGE_REFERENCE_PART_TYPE,
          ]),
      ),
      ...(selectedAssetReferences ?? []).map((reference) =>
        buildBrandAssistantSelectedAssetReferencePart(reference),
      ),
      ...(selectedImageReferences ?? []).map((reference) =>
        buildBrandAssistantSelectedImageReferencePart(reference),
      ),
    ] as UIMessage["parts"],
  };
};

function isBrandAssistantSelectedReferencePartType(part: unknown, types: readonly string[]) {
  return (
    typeof part === "object" &&
    part !== null &&
    "type" in part &&
    typeof part.type === "string" &&
    types.includes(part.type)
  );
}

export const attachSelectedAssetReferencesToUserMessage = (
  message: UIMessage | undefined,
  selectedAssetReferences: readonly BrandAssistantSelectedAssetReference[] | undefined,
): UIMessage | undefined =>
  attachSelectedReferencesToUserMessage(message, selectedAssetReferences, undefined);

export const buildBrandAssistantSendMessagesRequestBody = ({
  brandId,
  chatId,
  routeCatalog,
  currentPage,
  trigger,
  messages,
  selectedAssetReferences,
  selectedImageReferences,
}: {
  brandId: string;
  chatId: string;
  routeCatalog: readonly AssistantRouteCatalogEntry[];
  currentPage?: BrandAssistantCurrentPageContext | null;
  trigger: BrandAssistantChatRequestTrigger;
  messages: UIMessage[];
  selectedAssetReferences?: readonly BrandAssistantSelectedAssetReference[];
  selectedImageReferences?: readonly BrandAssistantSelectedImageReference[];
}) => {
  const base = {
    brandId,
    chatId,
    routeCatalog,
    ...(currentPage ? { currentPage } : {}),
    ...(selectedAssetReferences?.length ? { selectedAssetReferences } : {}),
    ...(selectedImageReferences?.length ? { selectedImageReferences } : {}),
  };

  if (trigger === "regenerate-message") {
    return {
      ...base,
      trigger,
      messages,
    };
  }

  return {
    ...base,
    message: attachSelectedReferencesToUserMessage(
      messages.at(-1),
      selectedAssetReferences,
      selectedImageReferences,
    ),
  };
};
